import { useEffect, useMemo, useRef, useState } from "react";
import { writeImproveHandoff } from "@/hooks/multi-agent";
import { useSpring, useTrail, animated } from "@react-spring/web";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apiBlob } from "@/services/studio/client";
import { artifactUrl } from "@/lib/studio/runs";
import { skillImpact } from "@/lib/studio/impact";
import {
  ChevronLeft, Clock, Download, FileJson, FileText, ListChecks, Loader2, Play, Square, Wrench,
} from "lucide-react";
import { EstimateBadge } from "@/components/organisms/studio/EstimateBadge";
import { RunStatusBadge } from "@/components/organisms/studio/RunStatusBadge";
import { SkillForm } from "@/components/organisms/studio/SkillForm";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import { PlaybookRunReportView } from "./PlaybookRunReportView";
import type { RunRecord } from "@/services/studio/types";
import { compactStageRows, progressFromEvents, stageBadge } from "@/components/organisms/studio/AgentRunProgress";
import type { StageStatus } from "@/components/organisms/studio/AgentRunProgress";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { useRun, useRuns, useSkill, useSurfaces } from "@/hooks/studio-platform";
import { useRunStore, getRunStore } from "@/stores/studio/runStore";
import { decodeBase64Json, encodeBase64Json, formatRelative } from "@/lib/studio/utils";
import type { EntityAction, RunStatusValue, Skill, SkillInput } from "@/services/studio/types";

// ── Labels ────────────────────────────────────────────────────────────────────
const SCREEN_LABELS: Record<string, string> = {
  "metadata-explorer": "Smart Metadata Explorer",
  "org-health": "Org Health Workspace",
  architecture: "Architecture Workspace",
  onboarding: "Onboarding Workspace",
  cleanup: "Cleanup Workspace",
  "object-explorer": "Object 360 Workspace",
  impact: "Impact Workspace",
  "data-quality": "Data Quality Workspace",
  codegen: "Code Generation Workspace",
  "apex-analyzer": "Apex 360 Workspace",
  "test-workspace": "Test Workspace",
  "refactor-workspace": "Refactor Workspace",
  "flow-analyzer": "Flow 360 Workspace",
  security: "Security Workspace",
  "test-dashboard": "Test Dashboard",
  deployment: "Deployment Workspace",
  runtime: "Runtime Workspace",
};

const TOOL_LABELS: Record<string, string> = {
  list_metadata_types: "Metadata DB",
  list_objects: "Metadata DB",
  list_classes: "Metadata DB",
  list_flows: "Metadata DB",
  list_perm_sets: "Metadata DB",
  list_metadata: "Metadata DB",
  get_object_fields: "Metadata DB",
  get_class_source: "RAG Workspace",
  read_metadata: "Metadata DB",
  graph_dependents: "Graph MCP",
  graph_impact: "Graph MCP",
  graph_object_usage: "Graph MCP",
  graph_calls: "Graph MCP",
  graph_triggers_on: "Graph MCP",
  graph_data_access: "Graph MCP",
  graph_detect_cycles: "Graph MCP",
  semantic_search: "Vector/RAG MCP",
  search_similar_classes: "Vector/RAG MCP",
  get_class_detail: "Agentic MCP",
  code_exec: "Code Exec MCP",
  llm_step: "LLM",
  query_records: "Salesforce Data",
  tooling_query: "Tooling API",
  run_apex_test: "Apex Test API",
};

function toolFamily(tool: string): string {
  if (tool.startsWith("salesforce_dx.")) return "Salesforce DX MCP";
  return TOOL_LABELS[tool] ?? "MCP Tool";
}

function humanize(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function durationLabel(ms: number | null | undefined): string {
  if (!ms || !Number.isFinite(ms) || ms <= 0) return "-";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ── Tab types ─────────────────────────────────────────────────────────────────
type WorkspaceTab = "overview" | "inputs" | "tools" | "runs" | "run";

// ── Page ──────────────────────────────────────────────────────────────────────
export function TemplateWorkspace() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const skillQ = useSkill(id);
  const surfacesQ = useSurfaces();
  const runsQ = useRuns({ skill_id: id, limit: 50 });

  const [activeTab, setActiveTab] = useState<WorkspaceTab>("overview");
  const [viewRunId, setViewRunId] = useState<string | null>(null);

  // This skill's own run store — instanced per skillId (like agentRunStore's
  // per-agent isolation), so a second migration doesn't evict this one. NOT reset
  // on unmount, so the run survives navigation.
  const runStoreStatus = useRunStore(id ?? "", (s) => s.status);
  const runStoreSkillId = useRunStore(id ?? "", (s) => s.skillId);
  const runStoreEvents = useRunStore(id ?? "", (s) => s.events);

  // Decode ?inputs= for the Run tab form pre-fill
  const initialInputs = useMemo<Record<string, unknown> | undefined>(() => {
    const raw = searchParams.get("inputs");
    if (!raw) return undefined;
    return decodeBase64Json<Record<string, unknown>>(raw) ?? undefined;
  }, [searchParams]);

  // Auto-view ?run_id= on mount — switch to Run tab and show that historical run.
  // Matches agents' AgentWorkspace deep-link effect exactly: this is the ONLY
  // thing that shows a report on load. Run history existing is not a trigger —
  // the default tab label is always "Run Migration" unless a run is currently
  // live or the user explicitly opens a specific run (here or via Run History
  // → "View report").
  const initialRunId = searchParams.get("run_id");
  const didAutoView = useRef(false);
  useEffect(() => {
    if (didAutoView.current) return;
    if (initialRunId) {
      didAutoView.current = true;
      setViewRunId(initialRunId);
      setActiveTab("run");
    }
  }, []);

  const surface = useMemo(
    () => surfacesQ.data?.surfaces.find((item) => item.skill_id === id),
    [id, surfacesQ.data?.surfaces],
  );
  const related = useMemo(() => {
    if (!surface) return [] as EntityAction[];
    return (surfacesQ.data?.surfaces ?? [])
      .filter((item) => item.skill_id !== surface.skill_id && item.screen === surface.screen)
      .slice(0, 6);
  }, [surface, surfacesQ.data?.surfaces]);

  // Tab content entrance spring
  const [contentSpring, contentSpringApi] = useSpring(() => ({
    opacity: 1,
    transform: "translateY(0px)",
    config: { tension: 380, friction: 28 },
  }));

  const triggerContentAnimation = () => {
    contentSpringApi.start({
      from: { opacity: 0, transform: "translateY(6px)" },
      to: { opacity: 1, transform: "translateY(0px)" },
    });
  };

  const isThisSkillRunning = runStoreSkillId === id && runStoreStatus === "running";
  const runProgress = useMemo(
    () => progressFromEvents(runStoreEvents),
    [runStoreEvents],
  );

  if (skillQ.isLoading || surfacesQ.isLoading) {
    return (
      <div className="flex h-full flex-col gap-3">
        <div className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card">
          <div className="shrink-0 border-b border-border bg-muted/20 px-5 py-4 space-y-2.5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-16 rounded loading-parallax bg-muted-foreground/10" />
                <span className="text-muted-foreground/20 text-sm">/</span>
                <div className="h-5 w-52 rounded-md loading-parallax bg-muted-foreground/10" />
              </div>
              <div className="h-7 w-28 rounded-md loading-parallax bg-muted-foreground/10" />
            </div>
            <div className="flex items-center gap-3">
              <div className="h-5 w-20 rounded-full loading-parallax bg-muted-foreground/10" />
              <div className="h-5 w-16 rounded-full border border-border/50 bg-muted-foreground/[0.04]" />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 border-b border-border px-3 py-0">
            {[80, 60, 52, 80, 70].map((w, i) => (
              <div key={i} className="mx-2 my-2.5 h-3 rounded loading-parallax bg-muted-foreground/10" style={{ width: w }} />
            ))}
          </div>
          <div className="flex-1 overflow-hidden p-5">
            <div className="grid gap-6 xl:grid-cols-[1fr_20rem] h-full">
              <div className="space-y-6">
                <div className="space-y-2">
                  <div className="h-2.5 w-24 rounded loading-parallax bg-muted-foreground/[0.07]" />
                  <div className="h-3.5 w-full rounded loading-parallax bg-muted-foreground/10" />
                  <div className="h-3.5 w-4/5 rounded loading-parallax bg-muted-foreground/[0.07]" />
                </div>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 w-full rounded-md border border-border/50 bg-muted-foreground/[0.03]" />
                ))}
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 rounded-lg border border-border/60 bg-muted/20" />
                  ))}
                </div>
                <div className="space-y-1.5">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-9 w-full rounded-md border border-border/50 bg-muted-foreground/[0.03]" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (skillQ.isError || !skillQ.data) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <p className="font-mono text-sm text-destructive">Could not load migration {id}.</p>
        <Link to="/migrations" className="mt-2 inline-block font-mono text-xs text-primary">
          ← back to migrations
        </Link>
      </div>
    );
  }

  const skill = skillQ.data;
  const runs = runsQ.data?.runs ?? [];

  const runTabPhase = isThisSkillRunning
    ? "running"
    : (runStoreSkillId === id && runStoreStatus !== "idle")
    ? "done"
    : "configure";

  return (
    <div className="flex h-full flex-col gap-3 animate-fade-in">
      <div className="flex flex-1 min-h-0 flex-col rounded-xl border border-border bg-card [overflow:clip]">

        {/* Header */}
        <div className="shrink-0 border-b border-border bg-muted/20 px-5 py-4 space-y-2">
          {/* Row 1: breadcrumb + Run Migration button */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-1.5">
              <Link
                to="/migrations"
                className="flex shrink-0 items-center gap-0.5 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronLeft className="h-3 w-3" />
                Migrations
              </Link>
              <span className="text-muted-foreground/30">/</span>
              <h1 className="truncate text-xl font-bold tracking-tight">{skill.title}</h1>
            </div>
            <Button
              variant="charcoal"
              size="sm"
              className="shrink-0"
              onClick={() => {
                setActiveTab("run");
                // Mirrors agents' header CTA: viewing a report is one-shot — any
                // click here while it's showing discards it and resets to the
                // configure form for a fresh run.
                getRunStore(id ?? "").getState().resetToConfigure();
                triggerContentAnimation();
              }}
            >
              <Play className="mr-1.5 h-3.5 w-3.5" />
              Run Migration
            </Button>
          </div>

          {/* Row 2: badges + estimate */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <div className="flex items-center gap-2">
              {skill.category && <Badge variant="warm" className="capitalize">{skill.category}</Badge>}
              <Badge variant={skillImpact(skill.category).writes ? "outline" : "secondary"}>{skillImpact(skill.category).label}</Badge>
              {surface?.panel && <Badge variant="outline">{surface.panel}</Badge>}
            </div>
            <EstimateBadge skill_id={skill.id} />
          </div>
        </div>

        {/* Live progress strip */}
        {isThisSkillRunning && (
          <div className="shrink-0 border-b border-border bg-muted/10 px-5 py-2.5">
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--success))]" />
                </span>
                <span className="truncate font-mono text-xs text-muted-foreground">Running…</span>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {runProgress.indeterminate
                    ? `${runProgress.completed} ${runProgress.completed === 1 ? "step" : "steps"} complete`
                    : `${runProgress.percent}%`}
                </span>
                <button
                  type="button"
                  onClick={() => getRunStore(id ?? "").getState().abort()}
                  className="inline-flex items-center gap-1 rounded-md border border-destructive/50 px-2 py-0.5 font-mono text-[11px] text-destructive transition-colors hover:bg-destructive/10"
                >
                  Stop
                </button>
              </div>
            </div>
            <div className="h-0.5 overflow-hidden rounded-full bg-muted">
              {/* No declared total — an animated sliver that travels, rather than a width
                  derived from a denominator the backend never sent. */}
              {runProgress.indeterminate ? (
                <div className="h-full w-1/3 animate-[pulse_1.6s_ease-in-out_infinite] rounded-full bg-[hsl(var(--success))]/60" />
              ) : (
                <div
                  className="h-full rounded-full bg-[hsl(var(--success))] transition-all duration-500"
                  style={{ width: `${runProgress.percent}%` }}
                />
              )}
            </div>
          </div>
        )}

        {/* Tab strip */}
        <div className="flex shrink-0 border-b border-border">
          {(["overview", "inputs", "tools", "runs"] as const).map((tabId) => {
            const labels: Record<string, string> = {
              overview: "Overview",
              inputs: "Inputs",
              tools: "Tools",
              runs: "Run History",
            };
            return (
              <button
                key={tabId}
                type="button"
                onClick={() => {
                  setActiveTab(tabId);
                  triggerContentAnimation();
                }}
                className={[
                  "px-5 py-2.5 text-xs transition-colors border-b-2",
                  activeTab === tabId
                    ? "border-primary font-medium text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                {labels[tabId]}
              </button>
            );
          })}

          <div className="mx-2 my-auto h-4 w-px bg-border/60" />

          {/* Run tab */}
          <button
            type="button"
            onClick={() => {
              setActiveTab("run");
              // Mirrors agents' tab-strip button: viewing a report is one-shot —
              // any click on this tab while it's showing "Report" discards it and
              // resets to the configure form for a fresh run.
              getRunStore(id ?? "").getState().resetToConfigure();
              triggerContentAnimation();
            }}
            className={[
              "flex items-center gap-1.5 px-4 py-2.5 text-xs transition-colors border-b-2",
              activeTab === "run"
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            ].join(" ")}
          >
            {runTabPhase === "running" ? (
              <>
                <span className="relative flex h-1.5 w-1.5 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--success))]" />
                </span>
                Running
              </>
            ) : runTabPhase === "done" ? (
              <>
                <FileText className="h-3 w-3 shrink-0" />
                Report
              </>
            ) : (
              <>
                <Play className="h-3 w-3 shrink-0" />
                Run Migration
              </>
            )}
          </button>
        </div>

        {/* Tab content */}
        <animated.div style={contentSpring} className="flex flex-1 min-h-0">
          {activeTab === "overview" && (
            <div className="flex-1 overflow-y-auto min-w-0">
              <OverviewTab skill={skill} runs={runs} surface={surface} related={related} />
            </div>
          )}
          {activeTab === "inputs" && (
            <div className="flex-1 overflow-y-auto min-w-0">
              <InputsTab inputs={skill.inputs ?? []} initialInputs={initialInputs ?? {}} />
            </div>
          )}
          {activeTab === "tools" && (
            <div className="flex-1 overflow-y-auto min-w-0">
              <ToolsTab tools={surface?.tools ?? skill.tools_used ?? []} />
            </div>
          )}
          {activeTab === "runs" && (
            <div className="flex-1 overflow-y-auto min-w-0">
              <RunsTab
                runs={runs}
                onViewRun={(runId) => {
                  setViewRunId(runId);
                  setActiveTab("run");
                  triggerContentAnimation();
                }}
                onSwitchToRunTab={() => {
                  setActiveTab("run");
                  triggerContentAnimation();
                }}
              />
            </div>
          )}
          {/* Run tab — always mounted so SSE stream stays alive */}
          <div className={["flex flex-1 min-h-0 min-w-0 flex-col", activeTab === "run" ? "" : "hidden"].join(" ")}>
            <RunTab
              skill={skill}
              initialInputs={initialInputs}
              viewRunId={viewRunId}
              onViewRunLoaded={() => setViewRunId(null)}
            />
          </div>
        </animated.div>
      </div>
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────
type RunSummaryItem = { id: string; status: string; started_at: string; total_ms?: number | null };

function OverviewTab({
  skill,
  runs,
  surface,
  related,
}: {
  skill: Skill;
  runs: RunSummaryItem[];
  surface: EntityAction | undefined;
  related: EntityAction[];
}) {
  return (
    <div className="grid gap-6 p-5 xl:grid-cols-[1fr_20rem]">
      {/* Left column */}
      <div className="space-y-6">
        <section>
          <SectionLabel>Description</SectionLabel>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
            {skill.description}
          </p>
        </section>

        {skill.prompt && (
          <section>
            <SectionLabel>Prompt Contract · v{skill.prompt_version ?? 1}</SectionLabel>
            <pre className="mt-2 max-h-48 overflow-auto rounded-lg border border-border bg-muted/20 p-3 whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-muted-foreground">
              {skill.prompt}
            </pre>
          </section>
        )}

        {/* Info grid */}
        <section>
          <SectionLabel>Details</SectionLabel>
          <div className="mt-2 grid gap-3 md:grid-cols-2">
            <InfoCard label="Workspace" value={surface ? SCREEN_LABELS[surface.screen] ?? surface.screen : "General"} />
            <InfoCard label="Works With" value={(surface?.entity_types ?? []).map(humanize).join(", ") || "Any metadata"} />
            <InfoCard label="When It Runs" value={humanize(surface?.trigger ?? "manual")} />
            <InfoCard label="Focus Area" value={surface?.panel ?? skill.category ?? "-"} />
          </div>
        </section>

        {/* Steps */}
        {(skill.steps ?? []).length > 0 && (
          <section>
            <SectionLabel>{skill.steps!.length} step{skill.steps!.length !== 1 ? "s" : ""} · Execution Plan</SectionLabel>
            <div className="mt-2 space-y-2">
              {skill.steps!.map((step, idx) => (
                <div
                  key={`${step.id}:${idx}`}
                  className="grid grid-cols-[2rem_minmax(0,1fr)] gap-3 rounded-md border border-border bg-muted/10 px-3 py-2.5"
                >
                  <span className="font-mono text-[11px] text-muted-foreground pt-0.5">{idx + 1}.</span>
                  <div>
                    <p className="font-mono text-xs font-semibold">{humanize(step.id)}</p>
                    <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">{toolFamily(step.tool)}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Related migrations */}
        {related.length > 0 && (
          <section>
            <SectionLabel>Related Migrations</SectionLabel>
            <div className="mt-2 space-y-2">
              {related.map((item) => (
                <Link
                  key={item.skill_id}
                  to={`/migrations/${encodeURIComponent(item.skill_id)}`}
                  className="block rounded-md border border-border bg-muted/10 px-3 py-2 hover:bg-muted/30 transition-colors"
                >
                  <div className="font-mono text-xs font-semibold">{item.title}</div>
                  <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">{item.panel}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Right column — sticky only in two-column layout */}
      <div className="xl:sticky xl:top-5 xl:self-start space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Inputs" value={String(skill.inputs?.length ?? 0)} icon={<FileJson className="h-3.5 w-3.5" />} />
          <StatCard label="Steps" value={String(skill.steps?.length ?? 0)} icon={<ListChecks className="h-3.5 w-3.5" />} />
          <StatCard label="Tools" value={String((surface?.tools ?? skill.tools_used ?? []).length)} icon={<Wrench className="h-3.5 w-3.5" />} />
          <StatCard label="Runs" value={String(runs.length)} />
        </div>

        <div>
          <SectionLabel>Recent Runs</SectionLabel>
          <div className="mt-2 space-y-1.5">
            {runs.slice(0, 6).map((run) => (
              <Link
                key={run.id}
                to={`/migrations/${encodeURIComponent(skill.id)}?run_id=${encodeURIComponent(run.id)}`}
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/10 px-3 py-2 hover:bg-muted/30 transition-colors"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="truncate font-mono text-xs">{formatRelative(run.started_at)}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {run.total_ms ? (
                    <span className="font-mono text-[10px] text-muted-foreground">{durationLabel(run.total_ms)}</span>
                  ) : null}
                  <RunStatusBadge status={run.status as RunStatusValue} />
                </div>
              </Link>
            ))}
            {runs.length === 0 && (
              <p className="font-mono text-xs text-muted-foreground py-2">No runs yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Inputs Tab ────────────────────────────────────────────────────────────────
function InputsTab({ inputs, initialInputs }: { inputs: SkillInput[]; initialInputs: Record<string, unknown> }) {
  if (!inputs.length) {
    return (
      <div className="p-5">
        <p className="text-sm text-muted-foreground">No inputs required for this migration.</p>
      </div>
    );
  }
  return (
    <div className="p-5">
      <SectionLabel>{inputs.length} field{inputs.length !== 1 ? "s" : ""}</SectionLabel>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[42rem] text-left font-mono text-xs">
          <thead className="text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-2 pr-3">Field</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Required</th>
              <th className="py-2 pr-3">Options</th>
              <th className="py-2 pr-4">Default Value</th>
            </tr>
          </thead>
          <tbody>
            {inputs.map((input) => (
              <tr key={input.name} className="border-b border-border last:border-0">
                <td className="py-2 pr-3 text-[hsl(var(--accent-warm))]">{input.label ?? input.name}</td>
                <td className="py-2 pr-3">{input.type}</td>
                <td className="py-2 pr-3">{input.required ? "yes" : "no"}</td>
                <td className="py-2 pr-3">{("source" in input && input.source) ? "Loaded from org" : "-"}</td>
                <td className="py-2 pr-4 break-words">{String(initialInputs[input.name] ?? input.default ?? "-")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tools Tab ─────────────────────────────────────────────────────────────────
function ToolsTab({ tools }: { tools: string[] }) {
  if (!tools.length) {
    return (
      <div className="p-5">
        <p className="text-sm text-muted-foreground">No tools listed for this migration.</p>
      </div>
    );
  }
  return (
    <div className="p-5">
      <SectionLabel>{tools.length} tool{tools.length !== 1 ? "s" : ""}</SectionLabel>
      <div className="mt-3 space-y-1.5">
        {tools.map((tool) => (
          <div
            key={tool}
            className="flex items-center gap-2 rounded-md border border-border bg-muted/10 px-3 py-2"
          >
            <Wrench className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 break-words font-mono text-xs">{tool}</span>
            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
              {toolFamily(tool)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Run History Tab ───────────────────────────────────────────────────────────
function RunsTab({
  runs,
  onViewRun,
  onSwitchToRunTab,
}: {
  runs: RunSummaryItem[];
  onViewRun: (runId: string) => void;
  onSwitchToRunTab: () => void;
}) {
  if (runs.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-20 text-center">
        <Clock className="mb-3 h-10 w-10 text-muted-foreground/25" />
        <p className="font-semibold">No runs yet</p>
        <p className="mt-1 text-sm text-muted-foreground">Run this migration to see history here.</p>
        <Button variant="charcoal" size="sm" className="mt-4" onClick={onSwitchToRunTab}>
          <Play className="mr-1.5 h-3.5 w-3.5" />
          Run Migration
        </Button>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
    <table className="w-full min-w-[36rem] text-sm">
      <thead>
        <tr className="border-b border-border">
          <th className="py-3 pl-5 pr-4 text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Time</th>
          <th className="py-3 pr-4 text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Status</th>
          <th className="py-3 pr-4 text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Duration</th>
          <th className="py-3 pr-5 text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Actions</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((run) => (
          <tr key={run.id} className="border-b border-border/50 last:border-0 transition-colors hover:bg-muted/20">
            <td className="py-3 pl-5 pr-4 font-mono text-xs">{formatRelative(run.started_at)}</td>
            <td className="py-3 pr-4">
              <RunStatusBadge status={run.status as RunStatusValue} />
            </td>
            <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">
              {durationLabel(run.total_ms)}
            </td>
            <td className="py-3 pr-5 text-left">
              <button
                type="button"
                onClick={() => onViewRun(run.id)}
                className="font-mono text-[11px] text-primary hover:underline"
              >
                View report
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

// ── Run Tab — 3-phase Agent-style (configure → running → done) ────────────────
function RunTab({
  skill,
  initialInputs,
  viewRunId,
  onViewRunLoaded,
}: {
  skill: Skill;
  initialInputs: Record<string, unknown> | undefined;
  viewRunId: string | null;
  onViewRunLoaded: () => void;
}) {
  const status = useRunStore(skill.id, (s) => s.status);
  const events = useRunStore(skill.id, (s) => s.events);
  const result = useRunStore(skill.id, (s) => s.result);
  const run_id = useRunStore(skill.id, (s) => s.run_id);
  const artifacts = useRunStore(skill.id, (s) => s.artifacts);
  const lastInputs = useRunStore(skill.id, (s) => s.inputs);
  const start = useRunStore(skill.id, (s) => s.start);
  const reconnect = useRunStore(skill.id, (s) => s.reconnect);
  const abort = useRunStore(skill.id, (s) => s.abort);
  const markHistoricalDone = useRunStore(skill.id, (s) => s.markHistoricalDone);
  const navigate = useNavigate();
  const [improving, setImproving] = useState(false);

  // Hand this run's report + artifacts to the multi-agent chat (same pattern as
  // the agent runs): fetch up to 5 artifacts, stash in sessionStorage, navigate.
  const improveInMultiAgent = async (
    report: string,
    arts: ReadonlyArray<{ name: string }>,
    runId: string,
    orgId: string | null,
  ) => {
    if (!runId) return;
    setImproving(true);
    const fetched: { name: string; content: string }[] = [];
    for (const a of arts.slice(0, 5)) {
      try {
        const { blob } = await apiBlob(artifactUrl(runId, a.name, orgId ?? undefined));
        fetched.push({ name: a.name, content: (await blob.text()).slice(0, 15000) });
      } catch { /* skip this artifact */ }
    }
    writeImproveHandoff({
      source: "migration",
      agentName: skill.title,
      report,
      artifacts: fetched,
      runId,
    });
    setImproving(false);
    navigate("/weave?seed=agent-result");
  };

  // Fetch persisted run for full result_md + artifacts after stream ends
  const persistedRunQ = useRun(run_id || undefined);
  const artifactOrgId = persistedRunQ.data?.run.org_id || null;
  const displayArtifacts =
    artifacts.length > 0 ? artifacts : (persistedRunQ.data?.artifacts ?? []);
  const displayRunId = run_id || persistedRunQ.data?.run.id || null;

  // Historical run view — tracks the run currently displayed (survives viewRunId clearing)
  const [historicalRunId, setHistoricalRunId] = useState<string | null>(null);
  const historicalRunQ = useRun(historicalRunId || viewRunId || undefined);
  const isLoadingHistorical = Boolean(
    (viewRunId || historicalRunId) && historicalRunQ.isLoading
  );

  // Guard against reconnecting the same run's live stream twice.
  const reconnectedId = useRef<string | null>(null);

  // When viewRunId is set and data arrives, either resume its LIVE stream (if the
  // run is still running/queued — e.g. reopened via ?run_id after a browser
  // refresh) or latch it into the static historical view. The latter stays visible
  // even after the parent clears viewRunId.
  useEffect(() => {
    if (!viewRunId) return;
    if (!historicalRunQ.data) return;
    const st = historicalRunQ.data.run.status;
    if ((st === "running" || st === "queued") && reconnectedId.current !== viewRunId) {
      reconnectedId.current = viewRunId;
      reconnect(skill.id, viewRunId, skill.title);
      onViewRunLoaded();
      return;
    }
    setHistoricalRunId(viewRunId);
    // Seed the shared store too — the workspace tab label reads runStore.status,
    // not this component's local historicalRunId, so without this the label falls
    // back to "Run Migration" the moment onViewRunLoaded() clears viewRunId below.
    markHistoricalDone(skill.id, viewRunId);
    onViewRunLoaded();
  }, [viewRunId, historicalRunQ.data]);

  // Derive phase from store status — historical view takes precedence
  const isHistoricalView = Boolean(historicalRunId || viewRunId);
  const phase: "configure" | "running" | "done" =
    status === "running" ? "running"
    : status === "idle" ? "configure"
    : "done";

  // Animate on every phase transition
  const [phaseSpring, phaseSpringApi] = useSpring(() => ({
    opacity: 1,
    transform: "translateY(0px)",
    config: { tension: 380, friction: 28 },
  }));
  const prevPhase = useRef(phase);
  useEffect(() => {
    if (prevPhase.current !== phase) {
      phaseSpringApi.start({
        from: { opacity: 0, transform: "translateY(6px)" },
        to: { opacity: 1, transform: "translateY(0px)" },
      });
      prevPhase.current = phase;
    }
  }, [phase]);

  const onSubmitted = (payload: Record<string, unknown>) => {
    // Mirror inputs to URL so the page is bookmarkable
    const url = new URL(window.location.href);
    url.searchParams.set("inputs", encodeURIComponent(encodeBase64Json(payload)));
    window.history.replaceState(null, "", url.toString());
  };

  const onRerun = () => {
    start(skill.id, lastInputs, skill.title);
  };

  // ── Historical run view ───────────────────────────────────────────────────────
  // When viewRunId is set (or just loaded), render the historical run inline.
  // This matches AgentRunTab's handleViewHistoricalRun + useAgentRun pattern.
  if (viewRunId || isHistoricalView) {
    const histData = historicalRunQ.data;
    const histRun = histData?.run;
    const histArtifacts = histData?.artifacts ?? [];
    const histOrgId = histRun?.org_id || null;

    // Loading state
    if (isLoadingHistorical) {
      return (
        <animated.div style={phaseSpring} className="flex flex-1 flex-col items-center justify-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/30" />
          <p className="font-mono text-xs text-muted-foreground">Loading report…</p>
        </animated.div>
      );
    }

    // Report loaded
    const histResult = histRun?.result_md || "";
    const histRunId = histRun?.id || historicalRunId || viewRunId || "";

    return (
      <animated.div style={phaseSpring} className="flex flex-1 min-h-0 min-w-0 flex-col overflow-y-auto">
        {histRun ? (
          <PlaybookRunReportView
            run={histRun}
            artifacts={histArtifacts}
            steps={histData?.steps ?? []}
            title={skill.title}
            improving={improving}
            onImprove={() => void improveInMultiAgent(histResult, histArtifacts, histRunId, histOrgId)}
          />
        ) : (
          <div className="p-5">
            <p className="font-mono text-xs text-muted-foreground">No report saved for this run.</p>
          </div>
        )}

        {/* Action row */}
        <div className="shrink-0 border-t border-border/50 px-5 py-4 flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHistoricalRunId(null)}
          >
            ← Back to run form
          </Button>
        </div>
      </animated.div>
    );
  }

  // ── Configure phase ──────────────────────────────────────────────────────────
  if (phase === "configure") {
    return (
      <animated.div style={phaseSpring} className="flex-1 overflow-y-auto min-h-0">
        <div className="grid gap-6 p-5 xl:grid-cols-[1fr_22rem]">
          {/* Left: form */}
          <div className="space-y-5">
            <div>
              <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {skill.inputs?.length
                  ? `${skill.inputs.length} input${skill.inputs.length === 1 ? "" : "s"} required`
                  : "No inputs required"}
              </p>
              <SkillForm
                skillId={skill.id}
                skillTitle={skill.title}
                inputs={skill.inputs ?? []}
                initialValues={initialInputs}
                onSubmitted={onSubmitted}
              />
            </div>
          </div>

          {/* Right: expected output */}
          <div className="xl:sticky xl:top-5 xl:self-start space-y-4">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              What You'll Get
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">{skill.description}</p>

            {/* Downloads preview */}
            <div>
              <p className="mb-2 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                Downloads after run
              </p>
              <div className="flex flex-wrap gap-1.5">
                {["md", "html", "json", "pdf"].map((fmt) => (
                  <span
                    key={fmt}
                    className="flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 font-mono text-xs"
                  >
                    <Download className="h-2.5 w-2.5 text-muted-foreground" />
                    .{fmt}
                  </span>
                ))}
              </div>
            </div>

            {/* Step count */}
            {(skill.steps ?? []).length > 0 && (
              <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
                <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                  Execution steps
                </p>
                <p className="mt-1 text-sm font-semibold">{skill.steps!.length} steps</p>
              </div>
            )}
          </div>
        </div>
      </animated.div>
    );
  }

  // ── Running phase ────────────────────────────────────────────────────────────
  if (phase === "running") {
    const rows = compactStageRows(events);
    const hasReport = Boolean(result);

    return (
      <animated.div style={phaseSpring} className="flex flex-1 min-h-0 flex-col">
        {hasReport ? (
          // Report streaming — collapse steps to dot bar, report fills the rest
          <>
            <RunningStepBar rows={rows} onStop={abort} />
            <div className="flex-1 overflow-y-auto p-5">
              <SkillResultMarkdown content={result} isStreaming={true} />
            </div>
          </>
        ) : (
          // No report yet — step cards are the hero
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 pt-5 pb-4">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {rows.length > 0 ? `Execution Steps · ${rows.length}` : "Starting…"}
              </p>
            </div>
            <div className="px-5 pb-5">
              {rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <Loader2 className="mb-3 h-8 w-8 animate-spin text-muted-foreground/25" />
                  <p className="font-mono text-xs text-muted-foreground">Connecting…</p>
                </div>
              ) : (
                <StepCardsGrid rows={rows} />
              )}
            </div>
          </div>
        )}
      </animated.div>
    );
  }

  // ── Done / error / cancelled phase ───────────────────────────────────────────
  const isError = status === "error" || status === "cancelled";
  // Build the run record for the shared report view from the persisted query
  // (same source the Reports page uses); synthesize from live state before it
  // resolves so the elaborated layout shows immediately.
  const displayRun = (persistedRunQ.data?.run
    ? { ...persistedRunQ.data.run, result_md: result || persistedRunQ.data.run.result_md }
    : displayRunId
      ? {
          id: displayRunId,
          skill_id: skill.id,
          org_id: artifactOrgId || "",
          surface_name: skill.title,
          status: isError ? "error" : "success",
          result_md: result,
          error_message: null,
          target_name: null,
          target_type: null,
          surface_category: "playbook",
          total_ms: null,
          started_at: "",
          inputs: lastInputs ?? {},
        }
      : null) as RunRecord | null;

  return (
    <animated.div style={phaseSpring} className="flex flex-1 min-h-0 min-w-0 flex-col overflow-y-auto">
      {displayRun ? (
        <PlaybookRunReportView
          run={displayRun}
          artifacts={displayArtifacts}
          steps={persistedRunQ.data?.steps ?? []}
          title={skill.title}
          improving={improving}
          onImprove={() => void improveInMultiAgent(result, displayArtifacts, displayRunId || "", artifactOrgId)}
        />
      ) : (
        <div className="p-5">
          <p className="font-mono text-xs text-muted-foreground">
            {isError ? "The run did not complete successfully." : "No report generated."}
          </p>
        </div>
      )}

      {/* Action row */}
      <div className="shrink-0 border-t border-border/50 px-5 py-4 flex items-center gap-3">
        <Button variant="charcoal" size="sm" onClick={onRerun}>
          <Play className="mr-1.5 h-3.5 w-3.5" />
          Run Again
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => getRunStore(skill.id).getState().reset()}
        >
          Edit Inputs
        </Button>
      </div>
    </animated.div>
  );
}

// ── Step cards grid ────────────────────────────────────────────────────────────
type StageRow = { stage: string; tools: string[]; status: StageStatus; error?: string };

function shortStageLabel(stage: string): string {
  return stage.split(" · ")[0].trim();
}

function StepCardsGrid({ rows }: { rows: StageRow[] }) {
  const trail = useTrail(rows.length, {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 340, friction: 28 },
  });

  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
      {trail.map((style, idx) => (
        <animated.div key={`${rows[idx].stage}-${idx}`} style={style} className="h-full">
          <StepCard row={rows[idx]} index={idx} />
        </animated.div>
      ))}
    </div>
  );
}

function StepCard({ row, index }: { row: StageRow; index: number }) {
  const isRunning = row.status === "running";
  const isDone = row.status === "done";
  const isError = row.status === "error";

  const accentClass = isRunning
    ? "border-l-[hsl(var(--accent-warm))]"
    : isDone ? "border-l-[hsl(var(--success))]"
    : isError ? "border-l-destructive"
    : "border-l-border/40";

  const [spring, api] = useSpring(() => ({ opacity: 1, config: { duration: 1200 } }));
  useEffect(() => {
    if (isRunning) {
      api.start({ to: [{ opacity: 0.55 }, { opacity: 1 }], loop: true, config: { duration: 1200 } });
    } else {
      api.stop();
      api.start({ opacity: 1, config: { duration: 200 } });
    }
  }, [isRunning]);

  return (
    <animated.div
      style={{ opacity: spring.opacity }}
      className={[
        "flex h-full min-h-[96px] flex-col rounded-xl border border-border/60 border-l-4 bg-card p-4",
        accentClass,
      ].join(" ")}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] text-muted-foreground/40">{index + 1}</span>
        {stageBadge(row.status)}
      </div>
      <p className="flex-1 font-mono text-sm font-semibold leading-snug line-clamp-2">
        {shortStageLabel(row.stage)}
      </p>
      {row.tools.length > 0 && (
        <p className="mt-2 truncate font-mono text-[10px] text-muted-foreground/50">
          {row.tools[0]}{row.tools.length > 1 ? ` +${row.tools.length - 1}` : ""}
        </p>
      )}
      {row.error && (
        <p className="mt-1 line-clamp-1 font-mono text-[10px] text-destructive">{row.error}</p>
      )}
    </animated.div>
  );
}

// ── Compact step dot bar (shown when report is streaming) ─────────────────────
function RunningStepBar({ rows, onStop }: { rows: StageRow[]; onStop: () => void }) {
  return (
    <div className="shrink-0 flex items-center gap-3 border-b border-border bg-muted/10 px-5 py-2.5">
      <div className="flex flex-1 flex-wrap items-center gap-1.5">
        {rows.map((row, idx) => (
          <span
            key={`${row.stage}-${idx}`}
            title={row.stage}
            className={[
              "h-2 w-2 rounded-full transition-colors",
              row.status === "running"
                ? "animate-ping bg-[hsl(var(--accent-warm))]"
                : row.status === "done"
                  ? "bg-[hsl(var(--success))]"
                  : row.status === "error"
                    ? "bg-destructive"
                    : "bg-muted-foreground/20",
            ].join(" ")}
          />
        ))}
        {rows.length > 0 && (
          <span className="ml-1 font-mono text-[10px] text-muted-foreground">
            {rows.filter((r) => r.status === "done").length}/{rows.length} steps
          </span>
        )}
      </div>
      <Button variant="destructive" size="sm" className="h-6 shrink-0 px-2.5 text-xs" onClick={onStop}>
        <Square className="mr-1 h-2.5 w-2.5" />
        Stop
      </Button>
    </div>
  );
}

// ── Small shared atoms ────────────────────────────────────────────────────────
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string; icon?: ReactNode }) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
      <div className="mb-1 flex items-center gap-1.5">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      </div>
      <p className="truncate text-sm font-semibold">{value}</p>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 break-words text-sm">{value || "-"}</div>
    </div>
  );
}
